import ExcelJS from 'exceljs';
import {
  BANG_CAN_DOI_PHAT_SINH_CONG_NO_EXPORT_TITLE,
  BANG_CAN_DOI_PHAT_SINH_CONG_NO_FILE_NAME,
  BANG_CAN_DOI_PHAT_SINH_CONG_NO_SHEET_NAME
} from './constant';
import { fetchAllBangCanDoiPhatSinhCongNo } from '@/hooks/queries/useBangCanDoiPhatSinhCongNo';
import { BANG_CAN_DOI_PHAT_SINH_CONG_NO_COLUMNS } from './column-mapping';
import type { ExcelExportOptions, ExportFromApiOptions } from './types';
import { formatDate } from '@/lib/formatUtils';

const buildFileName = (base: string) => {
  const timestamp = new Date().toISOString().slice(0, 10);
  return `${base}-${timestamp}.xlsx`;
};

const createWorkbook = (sheetName: string): { workbook: ExcelJS.Workbook; worksheet: ExcelJS.Worksheet } => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  return { workbook, worksheet };
};

const applyHeaderStyles = (row: ExcelJS.Row) => {
  row.eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } }
    };
  });
};

export const exportBangCanDoiPhatSinhCongNoExcel = async (
  rows: MuaHangRecord[] = [],
  options?: Partial<ExcelExportOptions<MuaHangRecord>>
) => {
  const columns = options?.columns || BANG_CAN_DOI_PHAT_SINH_CONG_NO_COLUMNS;
  const title = options?.title || BANG_CAN_DOI_PHAT_SINH_CONG_NO_EXPORT_TITLE;
  const subtitle = options?.subtitle;
  const sheetName = options?.sheetName || BANG_CAN_DOI_PHAT_SINH_CONG_NO_SHEET_NAME;
  const fileName = buildFileName(options?.fileName || BANG_CAN_DOI_PHAT_SINH_CONG_NO_FILE_NAME);

  const { workbook, worksheet } = createWorkbook(sheetName);

  worksheet.mergeCells(1, 1, 1, columns.length);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  if (subtitle) {
    worksheet.mergeCells(2, 1, 2, columns.length);
    const subtitleCell = worksheet.getCell(2, 1);
    subtitleCell.value = subtitle;
    subtitleCell.font = { italic: true, size: 11 };
    subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.addRow([]);
  } else {
    worksheet.addRow([]);
  }

  const headerRow = worksheet.addRow(columns.map(col => col.header));
  applyHeaderStyles(headerRow);

  rows.forEach(row => {
    const rowValues = columns.map(col => {
      const value = col.resolver(row);
      return value ?? '';
    });
    const excelRow = worksheet.addRow(rowValues);

    rowValues.forEach((value, colIndex) => {
      if (typeof value === 'number') {
        const cell = excelRow.getCell(colIndex + 1);
        cell.numFmt = '#,##0.00';
      }
    });
  });

  columns.forEach((col, index) => {
    worksheet.getColumn(index + 1).width = col.width || 18;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
};

export const exportBangCanDoiPhatSinhCongNoFromApi = async ({
  entitySlug,
  queryParams = {},
  pageSize = 200,
  excelOptions
}: ExportFromApiOptions) => {
  const { group_by, ...restQueryParams } = queryParams as MuaHangRecord;

  const payload = {
    ...restQueryParams,
    ...(group_by !== undefined && {
      group_by: Array.isArray(group_by) ? group_by.join(',') : group_by
    })
  };

  const rows = await fetchAllBangCanDoiPhatSinhCongNo(entitySlug, payload, pageSize);
  const from = formatDate((queryParams as MuaHangRecord).ngay_ct1 ?? null);
  const to = formatDate((queryParams as MuaHangRecord).ngay_ct2 ?? null);

  let subtitle: string | undefined;
  if (from && to) {
    subtitle = `Từ ngày ${from} đến ngày ${to}`;
  } else if (from) {
    subtitle = `Từ ngày ${from}`;
  } else if (to) {
    subtitle = `Đến ngày ${to}`;
  }

  return exportBangCanDoiPhatSinhCongNoExcel(rows, {
    ...excelOptions,
    subtitle
  });
};
